import React, { useEffect, useState } from 'react';
import { Checkbox, SparkIcon, TooltipTrigger, useReadOnly } from '@spark';

import { useAuth } from '@/features/auth/AuthContext';
import { formatDateTime, formatTime } from '@/shared/utils/helpers';
import './settings.css';

const RECOMMENDED_TOOLTIP =
    'The recommended provider is suggested to all users when they pick a teacher model.';

export function formatRecommendedMarkedAt(value) {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yy = String(date.getFullYear() % 100).padStart(2, '0');
    return `${dd}.${mm}.${yy}`;
}

function resolveDisplayName(user) {
    const name = (user?.name || '').trim();
    if (name) return name;
    const email = (user?.email || '').trim();
    if (email) return email;
    return 'Admin';
}

function resolveExactMarkedAtTooltip(value) {
    if (!value) return '';
    // Prefer time-only via shared helper; fall back to full datetime string.
    return formatTime(value) || formatDateTime(value) || '';
}

/**
 * Org-wide "Mark as recommended provider" row (Figma Model Providers).
 * Only one provider can be recommended at a time; checking another clears the previous.
 * When checked, shows "Marked by <username> DD.MM.YY" with exact time on date hover.
 * `disabled` lets callers block toggling when the provider has no credentials.
 */
const RecommendedProviderRow = ({
    provider,
    recommendedProvider,
    onRecommendedProviderUpdate,
    disabled = false,
}) => {
    const { user, role, isAuthEnabled } = useAuth();
    const isAdmin = !isAuthEnabled || role === 'Admin';
    const { readOnly } = useReadOnly();
    // undefined = trust server; otherwise optimistic override (null = cleared).
    const [localOverride, setLocalOverride] = useState(undefined);

    useEffect(() => {
        setLocalOverride(undefined);
    }, [recommendedProvider]);

    if (!onRecommendedProviderUpdate || !provider) {
        return null;
    }

    const active =
        localOverride !== undefined ? localOverride : recommendedProvider;
    const isRecommended = active?.provider === provider;
    const markedBy =
        (active?.marked_by || '').trim() || resolveDisplayName(user);
    const markedAtRaw = active?.marked_at || (isRecommended ? new Date() : null);
    const markedAt = formatRecommendedMarkedAt(markedAtRaw);
    const markedAtExact = resolveExactMarkedAtTooltip(markedAtRaw);

    const handleToggle = () => {
        if (isRecommended) {
            setLocalOverride(null);
            onRecommendedProviderUpdate(null);
            return;
        }
        setLocalOverride({
            provider,
            marked_by: resolveDisplayName(user),
            marked_at: new Date().toISOString(),
        });
        onRecommendedProviderUpdate(provider);
    };

    return (
        <div className="settings-recommended">
            <Checkbox
                label="Mark as recommended provider"
                status={isRecommended ? 'active' : 'inactive'}
                disabled={readOnly || !isAdmin || disabled}
                onChange={handleToggle}
            />
            <TooltipTrigger text={RECOMMENDED_TOOLTIP}>
                <span className="settings-recommended__help">
                    <SparkIcon slug="circle-help" size="small" />
                </span>
            </TooltipTrigger>
            {isRecommended ? (
                <span className="settings-recommended__marked-by">
                    {`Marked by ${markedBy}`}
                    {markedAt ? (
                        <>
                            {' '}
                            <TooltipTrigger text={markedAtExact || markedAt}>
                                <span className="settings-recommended__marked-at">
                                    {markedAt}
                                </span>
                            </TooltipTrigger>
                        </>
                    ) : null}
                </span>
            ) : null}
        </div>
    );
};

export default RecommendedProviderRow;
